/**
 * Onglet « Pièces reçues » : la collecte des factures reçues par courriel, du
 * relevé au dépôt dans le Drive. Réservé à l'équipe (droit `justificatifs.collecter`,
 * niveau administration : les pièces portent des noms et des montants).
 * Neuf sections, dans l'ordre du parcours ; chacune se relit seule.
 */

import { h } from '../../dom.js';
import { detailPiece, type RubriquePossible } from './api.js';
import { creerRetour, messageDe, type Contexte, type NomSection } from './communs.js';
import { sectionArbitrer } from './pieces-arbitrer.js';
import { sectionAvances } from './pieces-avances.js';
import { sectionHistorique } from './pieces-historique.js';
import { sectionRegles } from './pieces-regles.js';
import { creerSources } from './pieces-sources.js';
import { sectionDeposer, sectionTelecharger } from './pieces-telecharger.js';
import { sectionValider } from './pieces-valider.js';
import { sectionZoho } from './pieces-zoho.js';

export interface OngletPieces {
  readonly el: HTMLElement;
  /** Charge toutes les sections ; appelé à la première ouverture de l'onglet. */
  charger(): void;
}

export function ongletPiecesRecues(): OngletPieces {
  const retour = creerRetour();
  retour.el.classList.add('retour-collant');

  const rubriquesLues = new Map<string, Promise<RubriquePossible[]>>();
  /** Un exercice, une lecture : les rubriques viennent du Drive et ne changent pas d'une carte à l'autre. */
  const rubriques: Contexte['rubriques'] = (p) => {
    const cle = String(p.exercice ?? 'courant');
    let lue = rubriquesLues.get(cle);
    if (lue === undefined) {
      lue = detailPiece(p.id).then((d) => d.rubriquesPossibles);
      lue.catch(() => rubriquesLues.delete(cle));
      rubriquesLues.set(cle, lue);
    }
    return lue;
  };

  const chargeurs = new Map<NomSection, () => Promise<void>>();
  const recharger: Contexte['recharger'] = (...noms) => {
    // Les listes qu'une campagne ou « Actualiser » relit toutes ensemble relisent aussi les rubriques du Drive.
    if (noms.includes('telecharger')) rubriquesLues.clear();
    for (const nom of new Set(noms)) {
      chargeurs
        .get(nom)?.()
        .catch((e: unknown) => {
          retour.ko(messageDe(e, 'Une liste n’a pas pu être chargée. Actualisez la page.'));
        });
    }
  };
  const ctx: Contexte = { retour, recharger, rubriques };

  const valider = sectionValider(ctx);
  const arbitrer = sectionArbitrer(ctx);
  const telecharger = sectionTelecharger(ctx);
  const deposer = sectionDeposer(ctx);
  const zoho = sectionZoho(ctx);
  const avances = sectionAvances(ctx);
  const historique = sectionHistorique(ctx);
  const regles = sectionRegles(ctx);
  const sources = creerSources(ctx);

  chargeurs.set('valider', () => valider.charger());
  chargeurs.set('arbitrer', () => arbitrer.charger());
  chargeurs.set('telecharger', () => telecharger.charger());
  chargeurs.set('zoho', () => zoho.charger());
  chargeurs.set('avances', () => avances.charger());
  chargeurs.set('historique', () => historique.charger());
  chargeurs.set('regles', () => regles.charger());
  chargeurs.set('sources', () => sources.charger());

  const el = h(
    'div',
    { id: 'pieces-recues' },
    h(
      'div',
      { classe: 'section-intro' },
      h('p', { classe: 'note-lecture' }, 'Les factures arrivent par courriel : chaque jour, une campagne les relève, les trie, les lit et les propose. Rien n’est déposé dans le Drive sans que vous l’ayez validé, sauf règle contraire.'),
    ),
    retour.el,
    sources.entete,
    valider.section.el,
    arbitrer.section.el,
    telecharger.section.el,
    deposer.el,
    zoho.section.el,
    avances.section.el,
    historique.section.el,
    regles.section.el,
    sources.section.el,
  );

  return {
    el,
    charger() {
      recharger('sources', 'valider', 'arbitrer', 'telecharger', 'zoho', 'avances', 'historique', 'regles');
    },
  };
}
